import { Mic, MicOff, PhoneOff, RotateCcw } from 'lucide-react';

import { Spinner } from '@/shared/ui/spinner';

import { MioAudioControls } from './mio-audio-controls';

import type { VoiceDiagnostics, VoiceStatus } from '../voice/voice-session.service';

/** Voice controls live beside the shared transcript; typing and navigating remain available. */
export function MioCall({
  status,
  caption,
  muted,
  pending,
  diagnostics,
  microphoneId,
  onSelectMicrophone,
  onMute,
  onEnd,
  onResume,
}: {
  status: VoiceStatus;
  caption: string;
  muted: boolean;
  pending: boolean;
  diagnostics: VoiceDiagnostics | null;
  microphoneId: string;
  onSelectMicrophone: (deviceId: string) => Promise<void>;
  onMute: () => void;
  onEnd: () => void;
  onResume: () => void;
}) {
  const label =
    status === 'connecting'
      ? 'Connecting voice…'
      : status === 'paused'
        ? 'Voice paused'
        : muted
          ? 'Microphone muted'
          : status === 'speaking'
            ? 'Mio is speaking'
            : pending
              ? 'Mio is thinking'
              : 'Listening';
  return (
    <div className="border-mio-line bg-mio-soft mx-4 mb-3 rounded-lg border p-3">
      <div className="flex items-center gap-2">
        {status === 'connecting' || pending ? (
          <Spinner className="text-accent" />
        ) : (
          <span className="bg-mio size-1.5 shrink-0 rounded-full" aria-hidden="true" />
        )}
        <span className="text-fg flex-1 text-xs" role="status">
          {label}
        </span>
        {status === 'paused' ? (
          <button
            type="button"
            onClick={onResume}
            className="text-fg hover:bg-surface inline-flex size-11 shrink-0 items-center justify-center lg:size-8"
            aria-label="Reconnect voice"
          >
            <RotateCcw size={16} aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onMute}
            disabled={status === 'connecting'}
            aria-pressed={muted}
            aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
            className="text-fg hover:bg-surface inline-flex size-11 shrink-0 items-center justify-center disabled:opacity-40 lg:size-8"
          >
            {muted ? <MicOff size={16} aria-hidden="true" /> : <Mic size={16} aria-hidden="true" />}
          </button>
        )}
        <button
          type="button"
          onClick={onEnd}
          aria-label="End voice conversation"
          className="text-danger hover:bg-surface inline-flex size-11 shrink-0 items-center justify-center lg:size-8"
        >
          <PhoneOff size={16} aria-hidden="true" />
        </button>
      </div>
      {caption ? (
        <p
          className="text-fg-muted mt-2 max-h-24 overflow-auto text-xs leading-5"
          aria-label="Live voice caption"
        >
          {caption}
        </p>
      ) : (
        <p className="text-fg-subtle mt-1 text-xs">
          You can interrupt, type, or move between lessons.
        </p>
      )}
      <MioAudioControls
        diagnostics={diagnostics}
        microphoneId={microphoneId}
        status={status}
        muted={muted}
        onSelectMicrophone={onSelectMicrophone}
      />
    </div>
  );
}
